<template>
  <div class="editor-view-container overflow-hidden h-100">
    <EditorSkeletonLoader
      v-if="pageLoading"
      :side-by-side-view="!!sideBySideView"
    />

    <EditorLayout
      class="h-100 w-100"
      :side-by-side-view="sideBySideView"
    >
      <template
        v-if="draftsGranted"
        #full-width-header
      >
        <v-sheet
          max-width="1800px"
          class="mx-auto"
        >
          <SideBySideTopBar v-if="sideBySideView" />
          <ChromeTopBar v-else-if="isChromeView" />
          <TopBar v-else />
        </v-sheet>
      </template>

      <template
        v-if="!pageLoading"
        #content
      >
        <AppAlertsProvider v-if="isChromeView" />
        <JobProfileEditorContent
          v-if="draftsGranted"
          class="w-100"
        />
        <JobProfileLimitedContent
          v-else
        />
      </template>
      <template
        v-if="draftsGranted"
        #left
      >
        <LeftSideBar />
      </template>
      <template
        v-if="draftsGranted"
        #right
      >
        <RightSideBar v-if="!pageLoading" />
      </template>

      <template #content-footer>
        <EditorToolbarContainer
          class="pt-1 pb-1"
          :show-comments="true"
        />
      </template>
    </EditorLayout>

    <JobProfileCompareModal />
  </div>
</template>

<script setup>
import {
  nextTick, onMounted, watch, computed,
} from 'vue';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useAppLoading } from '@/stores/app/loading';
import { useAppAuthData } from '@/stores/app/authData';
import { useAppRoute } from '@/stores/app/route';
import { useCurrentJobProfile } from '@/stores/jobProfiles/currentJobProfile';
import { useFirstDraft } from '@/stores/document/firstDraft';
import { useSideBySideView } from '@/stores/document/sideBySideView';
import { useDocumentRightPanel } from '@/stores/document/rightPanel';
import EditorSkeletonLoader from '@/components/shared/SkeletonLoaders/editor/EditorSkeletonLoader.vue';
import EditorLayout from '@/components/editor/EditorLayout.vue';
import JobProfileEditorContent from '@/components/jobProfiles/editor/JobProfileEditorContent.vue';
import JobProfileLimitedContent from '@/components/jobProfiles/editor/JobProfileLimitedContent.vue';
import JobProfileCompareModal from '@/components/jobProfiles/editor/JobProfileCompareModal.vue';
import LeftSideBar from '@/components/jobProfiles/editor/LeftSideBar.vue';
import RightSideBar from '@/components/jobProfiles/editor/RightSideBar.vue';
import TopBar from '@/components/jobProfiles/editor/TopBar.vue';
import SideBySideTopBar from '@/components/jobProfiles/editor/SideBySideTopBar.vue';
import EditorToolbarContainer from '@/components/document/editorToolbar/EditorToolbarContainer.vue';
import ChromeTopBar from '@/components/jobProfiles/editor/ChromeTopBar.vue';
import AppAlertsProvider from '@/components/shared/alerts/AppAlertsProvider.vue';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  isHris: {
    type: Boolean,
    default: false,
  },
});

const router = useRouter();

const { pageLoading } = storeToRefs(useAppLoading());

const { checkRbacPermissions } = useAppAuthData();

const { route, isChromeView } = storeToRefs(useAppRoute());

const { sideBySideView } = storeToRefs(useSideBySideView());

const { summaryFieldId } = storeToRefs(useCurrentJobProfile());

const draftsGranted = computed(() => checkRbacPermissions(['initDraftProfileEditor']));

const load = async () => {
  const { loadJobProfile } = useCurrentJobProfile();
  await loadJobProfile({ ulid: props.id, isHris: props.isHris, rescore: true });
};

onMounted(() => {
  load();
});

watch(() => props.id, () => {
  load();
});

const VALID_SIDEBAR_TABS = ['history', 'flsa', 'approvals', 'comments', 'template-usage'];

watch(pageLoading, (loading) => {
  if (loading) return;

  const q = route.value?.query || {};
  const hasWriteWithFirstDraft = Boolean(q.writeWithFirstDraft);
  const sidebarTab = VALID_SIDEBAR_TABS.includes(q.sidebar) ? q.sidebar : null;

  if (!hasWriteWithFirstDraft && !sidebarTab) return;

  if (hasWriteWithFirstDraft) {
    useFirstDraft().generateAndApplyJobProfile({ ulid: props.id, summaryFieldId: summaryFieldId.value });
  }

  nextTick(() => {
    if (sidebarTab) {
      useDocumentRightPanel().setCurrentTab(sidebarTab);
    }

    if (hasWriteWithFirstDraft) {
      const nextQuery = { ...q };

      delete nextQuery.writeWithFirstDraft;

      router.replace({ query: nextQuery }).catch(() => {});
    }
  });
});
</script>
